import {
  BicycleSportsLight,
  Car1Light,
  RailroadTrainLight,
  WalkingLight,
} from '@ha/icons';
import React from 'react';
import { makeStyles } from '@material-ui/core';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Typography, Grid, units } from '@ha/dsl';
import LoadingButton from 'ha/components/next/LoadingButton';
import { useIntl } from 'ha/i18n';
import { track } from 'ha/modules/Analytics/actions';
import { TravelModeString } from 'ha/services/localyse';
import { SearchBarProvider, SearchBar } from 'ha/modules/SearchBar';
import { SimpleItem } from 'ha/components/next/LocationAutocompleteInput';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useLocation } from 'ha/helpers/router';
import { usePrevious } from 'ha/helpers/hooks/usePrevious';

import Select, { SelectOptionProps } from './Select';
import {
  getMapViewState,
  getCityCanonical,
  getCountryCode,
  getTravelTimePolygon,
  getTravelTimeCurrentInfo,
  getTravelTimeIsLoading,
} from '../../selectors';
import {
  fetchCoordinatesAndLoadPolygon,
  loadPolygon,
  PolygonActions,
  updateMapView,
} from '../../actions';
import { MapViewState } from '../../utils';

export const useStyles = makeStyles(theme => ({
  button: {
    position: 'absolute',
    top: 0,
  },
  selectLabel: {
    color: theme.palette.grey[600],
  },
  selectSpacing: {
    marginTop: theme.spacing(3),
  },
  selectLabelBottomSpacing: {
    marginBottom: theme.spacing(1),
  },
  icon: {
    fontSize: units.rem(1.2),
    marginRight: units.rem(0.5),
  },
  mobileButtonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    background: theme.palette.common.white,
  },
}));

interface Props {
  orientation?: 'vertical' | 'horizontal';
  onSubmit?: () => void;
  onReset?: () => void;
}

export const TravelTimeContentForm: React.FC<Props> = ({
  orientation = 'vertical',
  onSubmit,
  onReset,
}) => {
  const classes = useStyles();
  const { T } = useIntl();
  const dispatch = useDispatch();
  const { md } = useMediaQuery();
  const { query } = useLocation();

  const mapViewState = useSelector(getMapViewState);
  const cityCanonical = useSelector(getCityCanonical);
  const countryCode = useSelector(getCountryCode);
  const hasPolygon = useSelector(getTravelTimePolygon).length;
  const currentTravelTimeInfo = useSelector(getTravelTimeCurrentInfo);
  const isPolygonLoading = useSelector(getTravelTimeIsLoading);
  const prevIsPolygonLoading = usePrevious(isPolygonLoading);

  const [addressInfo, setAddressInfo] = React.useState<SimpleItem | null>(null);
  const [transport, setTransport] = React.useState<SelectOptionProps>();
  const [duration, setDuration] = React.useState<SelectOptionProps>();

  const isVertical = orientation === 'vertical';
  const areFieldsModified = React.useMemo(
    () =>
      (!!addressInfo?.placeId &&
        addressInfo.placeId !== currentTravelTimeInfo?.placeId) ||
      transport?.value !== currentTravelTimeInfo?.travelMode ||
      duration?.value !== currentTravelTimeInfo?.timeInSeconds,
    [addressInfo, transport, duration, currentTravelTimeInfo],
  );

  const transportationOptions = React.useMemo(
    () => [
      {
        title: T('search.map.commute.travel_mode.option_car'),
        icon: <Car1Light className={classes.icon} />,
        value: 'car',
        dataTestLocator: 'Travel Time Mode Car',
      },
      {
        title: T('search.map.commute.travel_mode.option_public_transport'),
        icon: <RailroadTrainLight className={classes.icon} />,
        value: 'transit',
      },
      {
        title: T('search.map.commute.travel_mode.option_bike'),
        icon: <BicycleSportsLight className={classes.icon} />,
        value: 'bike',
      },
      {
        title: T('search.map.commute.travel_mode.option_walk'),
        icon: <WalkingLight className={classes.icon} />,
        value: 'walk',
      },
    ],
    [T],
  );

  const durationOptions = React.useMemo(
    () => [
      {
        title: `10 ${T('search.map.commute.time.option_minutes')}`,
        value: 600,
        dataTestLocator: 'Travel Time Duration 10',
      },
      {
        title: `15 ${T('search.map.commute.time.option_minutes')}`,
        value: 900,
      },
      {
        title: `30 ${T('search.map.commute.time.option_minutes')}`,
        value: 1800,
      },
      {
        title: `45 ${T('search.map.commute.time.option_minutes')}`,
        value: 2700,
      },
      {
        title: `1 ${T('search.map.commute.time.option_hour')}`,
        value: 3600,
      },
    ],
    [T],
  );

  React.useEffect(() => {
    // In order the search input to be initialized correctly, we need to switch
    // to full map after we have fetched the polygons coordinates.
    if (
      md &&
      prevIsPolygonLoading &&
      !isPolygonLoading &&
      mapViewState !== MapViewState.fullMap
    ) {
      dispatch(updateMapView(MapViewState.fullMap));
    }
  }, [dispatch, md, mapViewState, prevIsPolygonLoading, isPolygonLoading]);

  React.useEffect(() => {
    if (!addressInfo && !transport && !duration && currentTravelTimeInfo) {
      const { placeId, travelMode, timeInSeconds, addressName } =
        currentTravelTimeInfo;
      setAddressInfo({
        id: placeId,
        label: addressName,
        placeId,
      });
      setTransport(
        transportationOptions.find(option => option.value === travelMode),
      );
      setDuration(
        durationOptions.find(option => option.value === Number(timeInSeconds)),
      );
    }
  }, [currentTravelTimeInfo, addressInfo, transport, duration]);

  const handleSubmit = React.useCallback(() => {
    if (addressInfo?.placeId && transport && duration) {
      // if the placeId has not been changed we skip the geocoder call
      if (
        addressInfo.placeId === currentTravelTimeInfo?.placeId &&
        currentTravelTimeInfo.placeCoordinates
      ) {
        dispatch(
          loadPolygon(
            currentTravelTimeInfo.placeId,
            currentTravelTimeInfo.placeCoordinates.lat,
            currentTravelTimeInfo.placeCoordinates.lng,
            transport.value as TravelModeString,
            Number(duration.value),
            addressInfo.label,
          ),
        );
      } else {
        dispatch(
          fetchCoordinatesAndLoadPolygon(
            addressInfo.placeId,
            transport.value as TravelModeString,
            Number(duration.value),
            addressInfo.label,
          ),
        );
      }

      dispatch(
        track('commute time applied', {
          mapType: !md ? 'mobileMap' : mapViewState,
          travelMode: transport.value,
          travelMinutes: Number(duration.value) / 60,
          locationAddress: addressInfo.label,
          cityCanonical,
          countryCode,
        }),
      );
    }

    onSubmit?.();
  }, [
    dispatch,
    md,
    addressInfo,
    transport,
    duration,
    mapViewState,
    cityCanonical,
    countryCode,
    onSubmit,
    currentTravelTimeInfo,
  ]);

  const handleReset = React.useCallback(() => {
    dispatch(PolygonActions.clear());
    onReset?.();
  }, [dispatch, onReset]);

  return (
    <Grid
      container
      justify="space-between"
      alignItems={isVertical ? 'stretch' : 'center'}
      spacing={isVertical ? 3 : 1}
      wrap={isVertical ? 'wrap' : 'nowrap'}
    >
      <Grid item xs={isVertical ? 12 : 5}>
        <SearchBarProvider
          value={{
            initialValue:
              currentTravelTimeInfo?.addressName || query.ttAddr || '',
            initialSuggestions: [],
          }}
        >
          <SearchBar
            placeholder={T('search.map.commute.location.hint')}
            onSubmit={address => setAddressInfo(address)}
            onReset={() => setAddressInfo(null)}
            predictionTypes={[]} // If nothing is specified, all types are returned.
          />
        </SearchBarProvider>
      </Grid>
      <Grid item xs={isVertical ? 12 : true}>
        <Grid
          container
          alignItems={isVertical ? 'stretch' : 'center'}
          spacing={isVertical ? 0 : 1}
          wrap={isVertical ? 'wrap' : 'nowrap'}
        >
          <Grid
            item
            xs={isVertical ? 12 : 'auto'}
            className={
              isVertical ? classes.selectLabelBottomSpacing : undefined
            }
          >
            <Typography.Paragraph className={classes.selectLabel}>
              {T('search.map.commute.travel_mode.label')}
            </Typography.Paragraph>
          </Grid>
          <Grid item xs={isVertical ? 12 : true}>
            <Select
              dataTestLocator="Travel Time Mode"
              label={T('search.map.commute.travel_mode.hint')}
              onChange={item => setTransport(item)}
              items={transportationOptions}
              selectedItem={transport}
            />
          </Grid>
          <Grid
            item
            xs={isVertical ? 12 : 'auto'}
            className={cx({
              [classes.selectSpacing]: isVertical,
              [classes.selectLabelBottomSpacing]: isVertical,
            })}
          >
            <Typography.Paragraph className={classes.selectLabel}>
              {T('search.map.commute.minutes.label')}
            </Typography.Paragraph>
          </Grid>
          <Grid item xs={isVertical ? 12 : true}>
            <Select
              dataTestLocator="Travel Time Duration"
              label={T('search.map.commute.minutes.hint')}
              onChange={item => setDuration(item)}
              items={durationOptions}
              selectedItem={duration}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={isVertical ? 12 : 'auto'}
        className={!md ? classes.mobileButtonContainer : undefined}
      >
        <Grid container justify="flex-end">
          {hasPolygon && !areFieldsModified ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleReset}
              fullWidth={!md}
            >
              {T('search.map.commute.cta.option_clear')}
            </Button>
          ) : (
            <LoadingButton
              buttonProps={{
                variant: 'contained',
                color: 'primary',
                onClick: handleSubmit,
                fullWidth: !md,
              }}
              kind={!md ? 'full-width' : undefined}
              loading={isPolygonLoading}
              disabled={!transport || !duration || !addressInfo}
            >
              {T('search.map.commute.cta.option_show')}
            </LoadingButton>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default React.memo(TravelTimeContentForm);
